// Startup: the session may well be valid, but the server could not confirm
// the identity (offline, unreachable, timed out, or a server error). The app
// is NOT shown — there is no confirmed user to show it for — and the session
// is NOT ended. The connection store keeps retrying in the background; the
// user can retry now or sign out explicitly.
import React from "react";
import { CloudOff, LogOut, RefreshCw, ServerCrash, ShieldCheck, WifiOff } from "lucide-react";
import {
  useSessionUi,
  useSecondsUntil,
  formatNumber,
  SessionScreenFrame,
  SessionCard,
  CardTitle,
  CardBody,
  ReferenceLine,
  IconTile,
} from "./SessionUi";

// The gateway could not reach the app server at all.
const GATEWAY_STATUSES = new Set([502, 504]);

/** offline | unavailable | serverError — what to tell the user. */
export const connectionProblemVariant = ({ identityStatus, connectionStatus, failure }) => {
  if (identityStatus === "offline" || connectionStatus === "offline") return "offline";
  const httpStatus = failure?.httpStatus;
  // A server that answered with an application error (500, or 503 from the
  // auth layer when its database lookup failed) is "having a problem";
  // gateway errors and no answer at all are "can't connect".
  if (httpStatus >= 500 && !GATEWAY_STATUSES.has(httpStatus)) return "serverError";
  return "unavailable";
};

export default function ConnectionProblemScreen({
  variant = "unavailable",
  referenceId = null,
  checking = false,
  nextRetryAt = null,
  onRetry,
  onSignOut,
  signingOut = false,
}) {
  const ui = useSessionUi();
  const { palette, t, language } = ui;
  const seconds = useSecondsUntil(nextRetryAt);

  const copy = {
    offline: { icon: WifiOff, title: t.offlineTitle, body: t.offlineBody },
    unavailable: { icon: CloudOff, title: t.unavailableTitle, body: t.unavailableBody },
    serverError: { icon: ServerCrash, title: t.serverErrorTitle, body: t.serverErrorBody },
  }[variant] || { icon: CloudOff, title: t.unavailableTitle, body: t.unavailableBody };

  // Countdown to the next automatic attempt; at 0 the attempt is starting.
  let statusLine = null;
  if (checking || seconds === 0) statusLine = t.checking;
  else if (seconds !== null) statusLine = t.retryingIn(formatNumber(seconds, language));

  return (
    <SessionScreenFrame ui={ui} label={copy.title}>
      <SessionCard ui={ui}>
        <div role="alert" aria-live="assertive">
          <IconTile icon={copy.icon} tone="warning" palette={palette} />
          <CardTitle ui={ui}>{copy.title}</CardTitle>
          <CardBody ui={ui}>{copy.body}</CardBody>
        </div>

        {/* The session is intact — say so plainly. */}
        <div
          style={{
            display: "flex", alignItems: "flex-start", gap: 10, marginTop: 18,
            padding: "12px 14px", borderRadius: 12,
            background: palette.accentSoft, border: `1px solid ${palette.accentBorder}`,
          }}
        >
          <ShieldCheck size={18} color={palette.accentText} aria-hidden="true" style={{ flexShrink: 0, marginTop: 1 }} />
          <span style={{ fontSize: 13.5, lineHeight: 1.55, color: palette.text }}>{t.sessionKept}</span>
        </div>

        <div
          aria-live="polite"
          style={{
            minHeight: 20, margin: "18px 0", display: "flex", alignItems: "center", justifyContent: "center",
            gap: 8, fontSize: 13, color: palette.textSecondary,
          }}
        >
          {statusLine && (
            <>
              <RefreshCw size={14} aria-hidden="true" className={checking || seconds === 0 ? "nx-session-spin" : undefined} />
              <span>{statusLine}</span>
            </>
          )}
        </div>

        <div className="nx-session-actions">
          <button
            type="button"
            className="nx-session-btn nx-session-btn--secondary"
            onClick={onSignOut}
            disabled={signingOut}
          >
            <LogOut size={16} aria-hidden="true" style={{ transform: ui.isRTL ? "scaleX(-1)" : "none" }} />
            {signingOut ? t.signingOut : t.signOut}
          </button>
          <button
            type="button"
            className="nx-session-btn nx-session-btn--primary"
            onClick={onRetry}
            disabled={checking || signingOut}
            autoFocus
          >
            <RefreshCw size={16} aria-hidden="true" className={checking ? "nx-session-spin" : undefined} />
            {t.retry}
          </button>
        </div>

        <ReferenceLine ui={ui} label={t.reference} value={referenceId} />
      </SessionCard>
    </SessionScreenFrame>
  );
}
